import { v4 as uuidv4 } from 'uuid';
import type {
  LineEvent,
  LineEventSubtype,
  ZoneEvent,
} from '../../../data-structures/sdmEventTypes';
import { type RealZoneOption } from '../../../hooks/useRealZoneOptions';

const SIM_CAM_IDS = ['sim-cam-front', 'sim-cam-drive-thru', 'sim-cam-lot'];
const SIM_STREAM_ID = 'sim-stream-1';
const SIM_PORT = '30003';

const ZONE_LABELS = [
  'Drive-Thru Queue',
  'Service Window',
  'Pickup Lane',
  'Parking Bay A',
  'Order Point',
];

const LINE_LABELS = ['Total Served', 'Arrivals', 'Departures', 'Lane Entry'];

let evtIdCounter = Date.now();
export function nextEvtId(): number {
  evtIdCounter += 1;
  return evtIdCounter;
}

// Monotonically increasing, human-readable id for "active entries" (manual, dual-preset, or
// program-opened zone visits) — shown directly in the UI so users can tell entries apart and
// target a specific one when closing. Never reset, so ids stay unique for the whole session.
let entrySeqCounter = 0;
export function nextEntryId(): string {
  entrySeqCounter += 1;
  return `Entry ${entrySeqCounter}`;
}

export function randomZoneLabel(): string {
  return ZONE_LABELS[Math.floor(Math.random() * ZONE_LABELS.length)];
}

export function randomLineLabel(): string {
  return LINE_LABELS[Math.floor(Math.random() * LINE_LABELS.length)];
}

export function randomCamId(): string {
  return SIM_CAM_IDS[Math.floor(Math.random() * SIM_CAM_IDS.length)];
}

export function randomPolygon(): { vertices: Array<[number, number]> } {
  return {
    vertices: [
      [0.1, 0.1],
      [0.9, 0.1],
      [0.9, 0.9],
      [0.1, 0.9],
    ],
  };
}

export function randomDurationMs(minSec: number, maxSec: number): number {
  return Math.floor((minSec + Math.random() * (maxSec - minSec)) * 1000);
}

export interface ZoneEventContext {
  camId: string;
  label: string;
  polygonId: string;
  objectId: number;
  vertices: Array<[number, number]>;
  /** Emitted as `metadata.zoneType` — which kind of zone-tracking event this is. */
  zoneType: string;
  /** False when `camId`/`polygonId` were matched from the site's real vision analytics
   * config rather than generated by the simulator (`sim-cam-*` / `sim-polygon-*`). */
  isSimulated: boolean;
}

/**
 * Builds the (camera, zone) identity shared by a zone entry and its eventual exit. When a
 * `real` zone option is supplied (a match from the site's line crossing configuration for
 * the currently selected AI tag scope), its real `camId`/`polygonId` are used instead of
 * generated simulator ids.
 */
export function newZoneContext(
  zoneType: string = 'queue',
  real?: RealZoneOption,
): ZoneEventContext {
  if (real) {
    return {
      camId: real.camId,
      label: real.label,
      polygonId: real.polygonId,
      objectId: Math.floor(Math.random() * 1_000_000) + 1,
      vertices: real.vertices,
      zoneType,
      isSimulated: false,
    };
  }
  return {
    camId: randomCamId(),
    label: randomZoneLabel(),
    polygonId: `sim-polygon-${uuidv4()}`,
    objectId: Math.floor(Math.random() * 1_000_000) + 1,
    vertices: randomPolygon().vertices,
    zoneType,
    isSimulated: true,
  };
}

export function buildZoneEntry(
  tagIds: string[],
  entryMs: number,
  ctx: ZoneEventContext,
): ZoneEvent {
  return {
    port: SIM_PORT,
    cam_id: ctx.camId,
    stream_id: SIM_STREAM_ID,
    evt_id: nextEvtId(),
    evt_type: 'zone',
    aiTagIds: tagIds,
    object_id: ctx.objectId,
    object_kind: 'vehicle',
    polygon: { vertices: ctx.vertices },
    min_threshold: 2000,
    label: ctx.label,
    subtype: 'dwell',
    entry_ms: entryMs,
    exit_ms: null,
    last_update_ms: entryMs,
    drive_off: null,
    metadata: {
      zoneType: ctx.zoneType,
      polygonId: ctx.polygonId,
      aiTagIds: tagIds,
    },
  };
}

export function buildZoneExit(
  tagIds: string[],
  entryMs: number,
  exitMs: number,
  ctx: ZoneEventContext,
): ZoneEvent {
  return {
    port: SIM_PORT,
    cam_id: ctx.camId,
    stream_id: SIM_STREAM_ID,
    evt_id: nextEvtId(),
    evt_type: 'zone',
    aiTagIds: tagIds,
    object_id: ctx.objectId,
    object_kind: 'vehicle',
    polygon: { vertices: ctx.vertices },
    min_threshold: 2000,
    label: ctx.label,
    subtype: 'exit',
    entry_ms: entryMs,
    exit_ms: exitMs,
    last_update_ms: exitMs,
    drive_off: false,
    metadata: {
      zoneType: ctx.zoneType,
      polygonId: ctx.polygonId,
      aiTagIds: tagIds,
    },
  };
}

export function buildDriveOffExit(
  tagIds: string[],
  entryMs: number,
  exitMs: number,
  ctx: ZoneEventContext,
): ZoneEvent {
  return {
    ...buildZoneExit(tagIds, entryMs, exitMs, ctx),
    drive_off: true,
  };
}

export interface LineCrossingBounce {
  id: number;
  start: number;
  end: number;
  sign: 1;
}

export interface LineCrossingOptions {
  subtype?: LineEventSubtype;
  bounces?: LineCrossingBounce[];
}

/** A couple of plausible bounce windows leading up to `whenMs`, for the "with bounces" preset. */
export function buildLineCrossingBounces(whenMs: number): LineCrossingBounce[] {
  const bounce = (startOffsetMs: number, endOffsetMs: number): LineCrossingBounce => ({
    id: nextEvtId(),
    start: whenMs - startOffsetMs,
    end: whenMs - endOffsetMs,
    sign: 1,
  });
  return [bounce(2500, 1800), bounce(1500, 600)];
}

export function buildLineCrossing(
  tagIds: string[],
  whenMs: number = Date.now(),
  options: LineCrossingOptions = {},
): LineEvent {
  const label = randomLineLabel();
  const camId = randomCamId();
  const objectId = Math.floor(Math.random() * 1_000_000) + 1;
  return {
    port: SIM_PORT,
    cam_id: camId,
    stream_id: SIM_STREAM_ID,
    evt_id: nextEvtId(),
    evt_type: 'line',
    aiTagIds: tagIds,
    start_ms: whenMs,
    end_ms: whenMs,
    event_line: {
      line: [0.5, 0.65, 0.8, 0.67],
      dir_ref: [0.65, 0.7],
      debounce_ms: 750,
      min_bounce_ms: 2000,
      label,
      neg_label: 'out',
      pos_label: 'in',
      subtype: options.subtype,
      metadata: { aiTagIds: tagIds },
      object_kinds: ['vehicle'],
      batched_kinds: ['person'],
      batch_size: 15,
      batch_timeout_ms: 300000,
    },
    intersections: [
      {
        id: objectId,
        object_id: objectId,
        t: whenMs,
        dir: 1,
        object_kind: 'vehicle',
        path: {
          points: [{ x: 0.66, y: 0.69, w: 0.35, h: 0.61, t: whenMs }],
        },
        evt_id: nextEvtId(),
      },
    ],
    bounces: options.bounces ?? [],
  };
}
